import { Contact } from "@boark/shared/types/contact";
import { SocialLinks } from "@boark/shared/types/social-link";
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faTwitter,
  faYoutube,
  IconDefinition,
} from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useLocale } from "../stores/locale";
import { formatPhoneNumber } from "../utils/phone";
import { Container } from "./container";

export function Footer({
  name,
  organizationNumber,
  contacts,
  socialLinks,
}: {
  name: string;
  organizationNumber?: string;
  contacts?: Contact[];
  socialLinks?: SocialLinks;
}) {
  const t = useLocale((state) => state.locale);

  return (
    <div className="min-h-[100px] pt-2 pb-4 px-5 bg-black text-white">
      <Container className="md:flex justify-between">
        <div className="flex flex-col flex-1/3">
          <span className="text-lg">{name}</span>
          {organizationNumber && (
            <span className="text-sm text-gray-200">
              {t.footer.organizationNumber}: {organizationNumber}
            </span>
          )}
        </div>
        <hr className="md:hidden mt-1 mb-2 border-gray-500" />
        <div className="flex flex-col flex-1/3">
          {contacts && (
            <>
              <span className="text-lg">{t.footer.contact}</span>
              <div className="flex gap-4 text-gray-200">
                {contacts
                  ?.filter((contact) => contact.showInFooter)
                  .map((contact, index) => (
                    <div key={index} className="flex flex-col">
                      <span className="font-bold">{contact.title}</span>
                      <span className="text-sm">{contact.name}</span>
                      <span className="text-sm">{contact.address}</span>
                      {contact.address2 && (
                        <span className="text-sm">{contact.address2}</span>
                      )}
                      <span className="text-sm">
                        {contact.postalNumber} {contact.city}
                      </span>
                      {contact.email && (
                        <span className="text-sm">
                          {t.footer.email}:{" "}
                          <a
                            className="underline"
                            href={`mailto:${contact.email}`}
                          >
                            {contact.email}
                          </a>
                        </span>
                      )}
                      {contact.phone && (
                        <span className="text-sm">
                          {t.footer.phone}:{" "}
                          <a
                            className="underline"
                            href={`tel:${contact.phone}`}
                          >
                            {formatPhoneNumber(contact.phone)}
                          </a>
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
        <hr className="md:hidden mt-1 mb-2 border-gray-500" />
        <div className="flex flex-col flex-1/3 md:items-end">
          {socialLinks && (
            <>
              <span className="text-lg">{t.footer.followUs}</span>
              <div className="flex gap-2 text-gray-200">
                {Object.entries(socialLinks).map(([key, link], index) => {
                  return (
                    <a
                      key={index}
                      className="hover:text-white"
                      href={link}
                      title={upperCaseFirstLetter(key)}
                    >
                      <SocialIcon type={key} />
                      <span className="sr-only">{key}</span>
                    </a>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </Container>
    </div>
  );
}

function SocialIcon({ type }: { type: string }) {
  let icon: IconDefinition | null = null;
  switch (type) {
    case "facebook":
      icon = faFacebook;
      break;
    case "instagram":
      icon = faInstagram;
      break;
    case "twitter":
      icon = faTwitter;
      break;
    case "linkedin":
      icon = faLinkedin;
      break;
    case "youtube":
      icon = faYoutube;
      break;
  }

  if (!icon) return null;

  return <FontAwesomeIcon icon={icon} size="lg" />;
}

function upperCaseFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
